import type { ReactNode } from "react";

export type ConfirmOptions = {
  /** Overrides for when "Cancel"/"Confirm" would read badly. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints Confirm red, for a confirmation that deletes, discards or revokes. */
  destructive?: boolean;
};

export type DialogService = {
  alert: (message: ReactNode, title?: string) => Promise<boolean>;
  confirm: (
    message: string,
    title?: string,
    options?: ConfirmOptions,
  ) => Promise<boolean>;
};

declare global {
  interface Window {
    dialogService?: DialogService;
  }
}

/** Replaces native window.alert with a custom dialog. */
export async function alert(
  message: ReactNode,
  title?: string,
): Promise<boolean> {
  if (window.dialogService?.alert) {
    return window.dialogService.alert(message, title);
  }

  window.alert(String(message));
  return true;
}

/** Replaces native window.confirm with a custom dialog. */
export async function confirm(
  message: string,
  title?: string,
  options?: ConfirmOptions,
): Promise<boolean> {
  if (window.dialogService?.confirm) {
    return window.dialogService.confirm(message, title, options);
  }

  // Fixed OK/Cancel buttons, so `options` is dropped.
  return window.confirm(message);
}
